import type { ApiType, KubernetesObject, V1Affinity, V1EnvVar, V1JobSpec, V1ObjectMeta, V1Pod, V1ResourceRequirements, V1SecurityContext } from "@kubernetes/client-node"
import type { AxiosRequestConfig, AxiosResponse } from "axios"
import type { LoDashStatic } from 'lodash'
import type { Logger } from 'pino'
import type { CoreOptions, Response } from 'request'
import type * as stream from "stream"

export type KV = {
  [key: string]: unknown
}

export type BasicType = string | number | boolean

export type KVString = {
  [key: string]: string
}

export type HandlerObject<T> = {
  install: OperateFunction<T> | (new () => Operator<T>)
  uninstall?: OperateFunction<T> | (new () => Operator<T>)
}

export interface PluginDefinition<T> {
  name: string,
  schema: unknown,
  settings: unknown,
  handlers: {
    [key: string]: OperateFunction<T> | (new () => Operator<T>) | HandlerObject<T> | OperatedContextFunction<T>
  },
  uninstaller?: OperateFunction<T> | (new () => Operator<T>)
  version: string
}

/**
 * The diff result between previous YAM and current YAM.
 * "path" is the object/array json path, when previous/current is null,
 * it indicates it was new created or deleted.
 */
export type DiffResult<T> = {
  currentItems: T[]

  hasDiff: boolean

  hasNew: boolean
  newItems: T[]

  hasDeleted: boolean
  deletedItems: T[]

  hasModified: boolean
  modifiedItems: { previous: T, current: T }[]
}

/**
 * Kubernetes Resource Metadata
 */
export type KindGroupVersion = {
  kind: string,
  apiGroup: string,
  name: string,
  version: string,
  namespacedRes: boolean
}

/**
 * PlanContext is provided in Plan stage for plugins
 */
export interface PlanContext {
  data: PlanContextData

  log: Logger

  _: LoDashStatic

  /**
   * Append actions to be executed in Apply stage.
   */
  action: (action: Action, name: string, thisArg?: Operator<unknown>) => void

  /**
   * Read files of YAM directory relative path, handle yaml includes and replace placeholders with values
   */
  renderTemplate: (relativePath: string, handleInclude: boolean) => Promise<string>

  /**
   * Load file content without rendering process
   */
  loadFile: (relativePath: string) => Promise<string | undefined>

}

/**
 * A container for preserve Plan stage inputs and outputs
 */
export type PlanContextData = {
  actions: Action[]

  customizedValues: KV
  appendParamLabels: KVString

  currentModelFull: IApplicationModel
  previousModelFull: IApplicationModel

  workingDir: string
  stackName: string

  envName: string
  envLocators: string[]

  kubeContextName: string
  kubeConfigPath?: string

  runMode: OperatorMode
  dryRun: boolean
  forceRestartWorkload: boolean
  templateOutput?: boolean
  partialPluginMode?: boolean
  multiEnvMode?: boolean

  app: string
  namespace: string

  workloadName: string
  serviceAccountName: string

  pluginInfoList: PluginInfoList
}


export type PluginInfoList = {
  [pluginName: string]: {
    version: string
    settingHash: string
  }
}

export type OperatorMode = 'plan-only' | 'apply-only' | 'plan-apply' | 'uninstall'


export type ContainerEnvVar = V1EnvVar & { containers?: string[] }
/**
 * In execute stage, all available actions are defined in ExecuteContext,
 * 
 */
export interface ExecuteContext {

  /**
   * In dry-run mode, no side effect, each plugin should implement dry-run mode
   * Side effect actions like fetchLogs / sendRequest / runJob will return with mocked result.
   */
  dryRunMode: boolean

  /**
   * If force is true, the corresponding pod will be forcibly recreated
   */
  forceRestartWorkload: boolean

  /**
   * KubernetesObject manipulation, core features of YAM.
   */
  mergeToYaml: (param: YamlTransformParam) => void

  removeFromYaml: (param: YamlTransformParam) => void

  /**
   * API call using Axios, for integrating will other systems.
   */
  sendRequest: (option: AxiosRequestConfig) => Promise<AxiosResponse>

  /**
   * Inject customized environment variables to Pods
   */
  containerEnvVariables: ContainerEnvVar[]

  /**
   * K8S Client, contains a set of pre-defined operations.
   */
  k8sClient: KubernetesOperator

  /**
   * Managed resources are KubernetesObject that will be applied to Cluster one by one.
   */
  managedK8SResources: Map<string, KV>

  log: Logger

  _: LoDashStatic
}

export type ApiConstructor<T extends ApiType> = new (server: string) => T

export type KubernetesObjectWithSpec = KubernetesObject & { spec: KV }

export type CheckableWorkloadType = K8SResourceType.Pod | K8SResourceType.Deployment |
  K8SResourceType.StatefulSet | K8SResourceType.DaemonSet | K8SResourceType.Job

export interface KubernetesOperator {
  find: <T>(resource: K8SResourceMeta, labelSelector?: KVString, cluster?: string) => Promise<T[]>
  apply: (resource: string | KubernetesObject | KubernetesObject[] | KubernetesObjectWithSpec, replaceMode?: boolean, cluster?: string) => Promise<void>
  delete: (resource: K8SResourceMeta, cluster?: string) => Promise<void>
  namespaceExists: (ns: string, cluster?: string) => Promise<boolean>

  getPods: (param: PodQueryParam, cluster?: string) => Promise<V1Pod[]>
  getConf<Query extends ConfQueryParam | SecretQueryParam>(param: Query, cluster?: string): Promise<GetConfReturn<Query> | undefined>
  saveConf: (conf: ConfigMap | Secret, cluster?: string) => Promise<void>

  checkWorkloadStatus: (type: CheckableWorkloadType, name: string, maxCheckTimeMills?: number, namespace?: string, cluster?: string) => Promise<void>
  runJob: (param: JobParam, cluster?: string) => Promise<void>
  portForward: (param: ForwardParam, cluster?: string) => Promise<ForwardOrExecResult>
  exec: (param: ExecParam, cluster?: string) => Promise<ForwardOrExecResult>
  fetchLogs: (param: FetchLogParam, cluster?: string) => Promise<string[] | undefined>
  crdExists: (kind: string, apiGroup?: string, cluster?: string) => boolean

  /**
   * Make customize API client to implement anything that couldn't be done by other pre-defined methods.
   */
  makeApiClient: <T extends ApiType> (apiClientType: ApiConstructor<T>, cluster?: string) => T

  /**
   * Factory method to create KubernetesResource object that kind/apiVersion/metadata are set by YAM engine
   */
  makeResource: <T extends KubernetesObject>  (
    name: string, kind: string | K8SResourceType, spec: unknown, apiVersion?: string,
    namespace?: string, annotations?: KVString, extraLabels?: KVString) => T

  getBuiltInLabels(): KVString

  /**
   * Resources that have been applied into cluster, key is the Kind
   */
  appliedK8SResources: Map<string, KubernetesObject[]>
  deletedK8SResources: Map<string, KubernetesObject[]>

  rawRequest: (path: string, method: string, reqBody?: unknown, cluster?: string, extraOpts?: CoreOptions) => Promise<{ body: unknown, response: Response }>
}

export type YamlTransformParam = {
  name: string

  /**
   * JSON path to apply the change, eg. a.b.c[2].d
   */
  jsonPath?: string | string[]

  /**
   * If sub-object of JSON path not found, throw error rather than ignore
   */
  failIfNotFound?: boolean

  content?: KV | KubernetesObject

  /**
   * By default, when merging arrays, will use Append mode, rather than replace whole array,
   * this is different from JSON Merge Patch (https://datatracker.ietf.org/doc/html/rfc7386).
   * 
   * If arrayReplaceMode set to true, will use JSON Merge Patch mode, replace the array.
   */
  arrayReplaceMode?: boolean

  /**
   * Defer mode means yaml patch will be executed after all handlers finished
   */
  defer?: boolean

}

export type K8SResourceMeta = {
  name?: string
  namespace?: string
  kind: K8SResourceType | string
  apiVersion?: string
}

export enum K8SResourceType {
  Pod = "Pod",
  Deployment = "Deployment",
  StatefulSet = "StatefulSet",
  DaemonSet = "DaemonSet",
  Job = "Job",
  CronJob = "CronJob",

  ConfigMap = "ConfigMap",
  Secret = "Secret",

  Namespace = "Namespace",
  LimitRange = "LimitRange",
  ResourceQuota = "ResourceQuota",
  Node = "Node",
  Event = "Event",

  NetworkPolicy = "NetworkPolicy",
  Ingress = "Ingress",
  Service = "Service",
  Endpoint = "Endpoint",

  StorageClass = "StorageClass",
  PersistentVolume = "PersistentVolume",
  PersistentVolumeClaim = "PersistentVolumeClaim",

  Role = "Role",
  ClusterRole = "ClusterRole",
  RoleBinding = "RoleBinding",
  ClusterRoleBinding = "ClusterRoleBinding",
  ServiceAccount = "ServiceAccount",

  ApiService = "ApiService",
  CustomResourceDefinition = "CustomResourceDefinition",

  HorizontalPodAutoScaler = "HorizontalPodAutoScaler",
  PodDisruptionBudget = "PodDisruptionBudget",

  // None native but commonly used resources
  ScaledObject = "ScaledObject",
  PrometheusRule = "PrometheusRule",

  ServiceMonitor = "ServiceMonitor",
  AlertmanagerConfig = "AlertmanagerConfig",

  Certificate = "Certificate",
  Issuer = "Issuer",
  ClusterIssuer = "ClusterIssuer"

}

export type PodQueryParam = {
  podName?: string
  appLabelName?: string
  labelSelectors?: KVString
  namespace?: string
}

export type ConfQueryParam = {
  name: string
  namespace?: string
  secret?: false
}

export type SecretQueryParam = {
  name: string
  namespace?: string
  secret: true
}

export type GetConfReturn<Q extends { secret?: boolean }> =
  Q['secret'] extends true ? Secret : ConfigMap

type Conf = {
  name: string
  data: KVString
  namespace?: string
  metadata?: V1ObjectMeta
}

export type ConfigMap = Conf & {
  secret?: false
}

export type Secret = Conf & {
  secret: true
  // secret type, defaults to Opaque
  type?: string
}

export type JobParam = {
  name: string
  namespace?: string
  simpleSpec?: SimplePodSpec
  jobSpec?: V1JobSpec
  followJobLog?: NodeJS.WritableStream
}

export type SimplePodSpec = {
  image: string
  command?: string[]
  arguments?: string[]
  resources?: V1ResourceRequirements

  env?: V1EnvVar[]
  configMapMount?: { name: string, mountPath: string }[]
  secretMount?: { name: string, mountPath: string }[]
  affinity?: V1Affinity

  hostNetwork?: boolean
  hostPid?: boolean
  hostIPC?: boolean

  securityContext?: V1SecurityContext
  serviceAccountName?: string
}

export type ForwardParam = {
  podName?: string
  labelSelectors?: KVString
  namespace?: string
  targetPort?: number
  containerPort: number
  maxCheckTimes?: number
  bindAll?: boolean
}

export type ForwardOrExecResult = {
  targetPod: string
  stop: () => Promise<void>
}

export type ExecParam = {
  podName?: string
  labelSelectors?: KVString
  namespace?: string

  container: string
  command?: string[]
  stdin?: stream.Readable
  stdout?: stream.Writable
  stderr?: stream.Writable
  onExit?: (code: number, reason: string) => void
}

export type FetchLogParam = {
  pod: string
  limit?: number
  previous?: boolean,
  namespace?: string
  tail?: boolean
  sinceSeconds?: number
  prependTimestamp?: boolean
  containers?: string[]
  followAndPipeTo?: NodeJS.WritableStream,
  followTimeout?: number,
  onComplete?: (err?: Error) => void
}

/**
 * An action is a specific operation generated in plan stage
 */
export type Action = (ctx: ExecuteContext) => Promise<void>

/**
 * OperateFunction and Operator is a common interface for implementing YAM handlers.
 */
export type OperateFunction<T> = (plan: PlanContext, diff: DiffResult<T>, settings: KV) => Promise<void>

export type OperatedContextFunction<T> = (plan: ExecuteContext, diff: DiffResult<T>, settings: KV) => Promise<void>

export interface Operator<T> {
  operate(plan: PlanContext, diff: DiffResult<T>, settings: KV): Promise<void>
}

export interface IMetadata {
  /**
   * Your Application Name
   */
  app: string
  /**
   * Your Team / Namespace
   */
  namespace: string
  /**
   * The owner of this application
   */
  owner?: string
  /**
   * The code repo of this application
   */
  repo?: string
  /**
   * The brief description of your application
   */
  description?: string
  [k: string]: unknown
}

export interface IApplicationModel {
  /**
   * User defined metadata
   */
  metadata: IMetadata

  /**
   * Describe prerequisites and dependencies of your application
   */
  prepare?: unknown[]

  /**
   * Describe configurations and secrets of your application
   */
  config?: unknown[]

  /**
   * Describe workload specification
   */
  deploy?: unknown

  access?: unknown[]

  observe?: {
    monitor: unknown
    alert: unknown
    [k: string]: unknown
  }

  scale?: unknown

  [k: string]: unknown
}